import { FolderIcon, FolderOpenIcon, LinkIcon, UsersIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Avatar } from '@/shared/components/avatar';
import { FileIcon } from '@/shared/components/files';
import { cn } from '@/shared/lib/cn';
import { SimpleTooltip } from '@/shared/ui/tooltip';

import type { DiskItem, DiskRole, ShareRole, Storage } from '../api';
import { STORAGE_ICONS } from '../hooks/useStorageName';
import { thumbnailUrl } from '../lib/items';

/** Folder glyph in the disk's own tint (amber, like most file managers), readable in both themes. */
export function FolderGlyph({
  size = 'md',
  open = false,
  shared = false,
  className,
}: {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  open?: boolean;
  shared?: boolean;
  className?: string;
}) {
  const Icon = open ? FolderOpenIcon : FolderIcon;
  return (
    <span
      aria-hidden
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center rounded-md bg-[oklch(0.8_0.14_80/0.16)] text-[oklch(0.62_0.15_70)] dark:text-[oklch(0.8_0.13_80)]',
        size === 'sm' && 'size-7 [&>svg]:size-3.5',
        size === 'md' && 'size-9 [&>svg]:size-[1.125rem]',
        size === 'lg' && 'size-12 rounded-lg [&>svg]:size-6',
        size === 'xl' && 'size-16 rounded-xl [&>svg]:size-8',
        className,
      )}
    >
      <Icon fill="currentColor" fillOpacity={0.18} strokeWidth={1.75} />
      {shared && (
        <span className="absolute -right-1 -bottom-1 flex size-4 items-center justify-center rounded-full bg-background ring-1 ring-border">
          <UsersIcon className="size-2.5 text-muted-foreground" />
        </span>
      )}
    </span>
  );
}

/** Icon of an item: folder glyph, the image's thumbnail, or the file kind's icon. */
export function ItemIcon({
  item,
  size = 'md',
  thumbnail = true,
  className,
}: {
  item: Pick<DiskItem, 'type' | 'name' | 'contentType' | 'file' | 'isShared'>;
  size?: 'sm' | 'md' | 'lg';
  thumbnail?: boolean;
  className?: string;
}) {
  const [broken, setBroken] = useState(false);
  if (item.type === 'folder') return <FolderGlyph size={size} shared={item.isShared} className={className} />;
  const thumb = thumbnail && !broken ? thumbnailUrl(item, 64) : null;
  if (thumb) {
    return (
      <img
        src={thumb}
        alt=""
        loading="lazy"
        onError={() => setBroken(true)}
        className={cn(
          'shrink-0 rounded-md bg-muted object-cover ring-1 ring-border/60',
          size === 'sm' && 'size-7',
          size === 'md' && 'size-9',
          size === 'lg' && 'size-12 rounded-lg',
          className,
        )}
      />
    );
  }
  return <FileIcon name={item.name} contentType={item.contentType} size={size} className={className} />;
}

/** Small square avatar of a drive (group avatar, drive glyph). */
export function StorageAvatar({
  storage,
  size = 'sm',
  className,
}: {
  storage: Pick<Storage, 'kind' | 'group' | 'owner'>;
  size?: 'xs' | 'sm' | 'md';
  className?: string;
}) {
  if (storage.kind === 'group' && storage.group) {
    return (
      <Avatar
        name={storage.group.name}
        src={storage.group.avatarUrl}
        seed={storage.group.id}
        size={size}
        shape="rounded"
        className={className}
      />
    );
  }
  const Icon = STORAGE_ICONS[storage.kind];
  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground',
        size === 'xs' && 'size-5 [&>svg]:size-3',
        size === 'sm' && 'size-6 [&>svg]:size-3.5',
        size === 'md' && 'size-8 [&>svg]:size-4',
        className,
      )}
    >
      <Icon />
    </span>
  );
}

/** "Просмотр" / "Редактирование" chip of a share or an effective role. */
export function RoleBadge({ role, className }: { role: ShareRole | DiskRole; className?: string }) {
  const { t } = useTranslation('disk');
  const edit = role === 'editor' || role === 'edit' || role === 'manage';
  return (
    <span
      className={cn(
        'inline-flex h-5 shrink-0 items-center rounded-full px-2 text-[0.6875rem] font-medium',
        edit ? 'bg-brand-soft text-brand-text' : 'bg-muted text-muted-foreground',
        className,
      )}
    >
      {t(`roles.${role}`)}
    </span>
  );
}

/** Shared / public link markers next to a name. */
export function ItemMarkers({
  item,
  className,
}: {
  item: Pick<DiskItem, 'isShared' | 'hasPublicLink'>;
  className?: string;
}) {
  const { t } = useTranslation('disk');
  if (!item.isShared && !item.hasPublicLink) return null;
  return (
    <span className={cn('inline-flex shrink-0 items-center gap-1 text-muted-foreground', className)}>
      {item.isShared && (
        <SimpleTooltip content={t('markers.shared')}>
          <span className="inline-flex" aria-label={t('markers.shared')} role="img">
            <UsersIcon className="size-3.5" aria-hidden />
          </span>
        </SimpleTooltip>
      )}
      {item.hasPublicLink && (
        <SimpleTooltip content={t('markers.publicLink')}>
          <span className="inline-flex" aria-label={t('markers.publicLink')} role="img">
            <LinkIcon className="size-3.5" aria-hidden />
          </span>
        </SimpleTooltip>
      )}
    </span>
  );
}
